import type { Shape } from '../../sim/shapes';
import type {
  AreaSpec,
  HitSpec,
  ProjectileSpec,
  SkillAction,
  SkillDef,
  SkillStage,
  StatusApply,
} from '../../sim/skillTypes';

/** Small builders that keep class and monster kits readable as data. */

export const deg = (d: number): number => (d * Math.PI) / 180;

export const cone = (radius: number, angleDeg: number, rotationDeg = 0): Shape => ({
  kind: 'cone',
  radius,
  angle: deg(angleDeg),
  ...(rotationDeg ? { rotation: deg(rotationDeg) } : {}),
});
export const circle = (radius: number, offset = 0): Shape => ({ kind: 'circle', radius, ...(offset ? { offset } : {}) });
export const rect = (length: number, width: number, offset = 0, rotationDeg = 0): Shape => ({
  kind: 'rect',
  length,
  width,
  ...(offset ? { offset } : {}),
  ...(rotationDeg ? { rotation: deg(rotationDeg) } : {}),
});
export const ring = (inner: number, outer: number, offset = 0): Shape => ({ kind: 'ring', inner, outer, ...(offset ? { offset } : {}) });

export const hit = (at: number, shape: Shape, coef: number, extra: Partial<HitSpec> = {}): SkillAction => ({
  at,
  kind: 'hit',
  hit: { shape, coef, ...extra },
});

/** Multi-hit: `count` hits spread `interval` apart dealing `totalCoef` in total. */
export const hits = (at: number, count: number, interval: number, shape: Shape, totalCoef: number, extra: Partial<HitSpec> = {}): SkillAction => ({
  at,
  kind: 'hit',
  hit: { shape, coef: totalCoef, count, interval, ...extra },
});

export const move = (at: number, distance: number, duration: number, opts: { toPoint?: boolean; ignoreCollision?: boolean } = {}): SkillAction => ({
  at,
  kind: 'move',
  distance,
  duration,
  ...opts,
});

export const teleport = (at: number, distance: number, toPoint = true): SkillAction => ({ at, kind: 'teleport', distance, toPoint });
export const area = (at: number, spec: AreaSpec): SkillAction => ({ at, kind: 'area', area: spec });
export const proj = (at: number, spec: ProjectileSpec): SkillAction => ({ at, kind: 'projectile', proj: spec });
export const self = (at: number, ...status: StatusApply[]): SkillAction => ({ at, kind: 'self', status });
export const party = (at: number, radius: number, ...status: StatusApply[]): SkillAction => ({ at, kind: 'party', radius, status });
export const invuln = (at: number, duration: number): SkillAction => ({ at, kind: 'invuln', duration });
export const identityOp = (at: number, op: string, value?: number): SkillAction => ({ at, kind: 'identity', op, ...(value !== undefined ? { value } : {}) });
export const counterWindow = (at: number, duration: number): SkillAction => ({ at, kind: 'counterWindow', duration });
export const announce = (at: number, text: string, style: 'mechanic' | 'warning' | 'boss' = 'mechanic'): SkillAction => ({ at, kind: 'announce', text, style });

export const stage = (anim: string, duration: number, actions: SkillAction[], opts: Partial<Omit<SkillStage, 'anim' | 'duration' | 'actions'>> = {}): SkillStage => ({
  anim,
  duration,
  actions: [...actions].sort((a, b) => a.at - b.at),
  ...opts,
});

type SkillInput = Pick<SkillDef, 'id' | 'name' | 'description' | 'castType' | 'cooldown' | 'mana' | 'stages'> & Partial<SkillDef>;

/** Fills the boring defaults of a SkillDef. */
export function skill(s: SkillInput): SkillDef {
  return {
    icon: s.id,
    superArmor: 'none',
    stagger: 'none',
    positional: 'none',
    weakPoint: 0,
    kind: 'skill',
    ...s,
  };
}

/** Standard 3-hit basic attack chain (0 MP, flinches mobs). */
export function basicAttack(id: string, name: string, hue: number, opts: { radius?: number; angle?: number; coefs?: [number, number, number]; ranged?: ProjectileSpec } = {}): SkillDef {
  const r = opts.radius ?? 2.8;
  const a = opts.angle ?? 110;
  const [c1, c2, c3] = opts.coefs ?? [0.45, 0.45, 0.8];
  const mk = (anim: string, dur: number, at: number, coef: number, last: boolean): SkillStage =>
    opts.ranged
      ? stage(anim, dur, [{ at, kind: 'projectile', proj: { ...opts.ranged, hit: { ...opts.ranged.hit, coef } } }])
      : stage(anim, dur, [hit(at, cone(r + (last ? 0.3 : 0), a + (last ? 20 : 0)), coef, { reaction: last ? { kind: 'knockback', power: 1.2 } : { kind: 'flinch' } })]);
  return skill({
    id,
    name,
    description: 'Three-strike basic attack. Costs no mana.',
    castType: 'combo',
    cooldown: 0,
    mana: 0,
    kind: 'basic',
    hue,
    followWindow: 0.45,
    identityGain: 1,
    stages: [mk('Attack1', 0.38, 0.14, c1, false), mk('Attack2', 0.38, 0.14, c2, false), mk('Attack3', 0.5, 0.2, c3, true)],
  });
}

/** Class movement skill on Space. */
export function dashSkill(id: string, name: string, hue: number, cooldown: number, distance: number, opts: { teleport?: boolean; duration?: number } = {}): SkillDef {
  const dur = opts.duration ?? 0.32;
  return skill({
    id,
    name,
    description: `${opts.teleport ? 'Blink' : 'Dash'} ${distance} m toward the cursor. Immune to push and crowd control; briefly invulnerable.`,
    castType: 'normal',
    cooldown,
    mana: 0,
    kind: 'dash',
    hue,
    superArmor: 'full',
    stages: [
      stage('Dash', dur + 0.05, opts.teleport ? [invuln(0, 0.25), teleport(0.05, distance, false)] : [invuln(0, 0.22), move(0, distance, dur, { ignoreCollision: true })], {
        cancelAt: dur * 0.85,
      }),
    ],
  });
}
